import { useInsuranceQuote } from '@/hooks/queries/onboarding/useInsuranceQuote'
import { useRespondToQuote } from '@/hooks/mutations/onboarding/useRespondToQuote'
import { useTenantNavigation } from '@/hooks/useTenantNavigation'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CenteredLoading,
} from '@repo/ui/components'
import { formatCurrency } from '@repo/ui/lib'
import { formatUsDate } from '@repo/utils'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangleIcon, ClockIcon, ShieldCheckIcon, XCircleIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/carriers/$slug/driver-onboarding/insurance-quote')({
  component: InsuranceQuotePage,
  staticData: {
    title: 'route.insuranceQuote',
  },
})

function InsuranceQuotePage() {
  const { t } = useTranslation('ui')
  const { slug, navigate } = useTenantNavigation()
  const { data, isLoading, error } = useInsuranceQuote(true)
  const respondToQuote = useRespondToQuote()
  const [pageLoadTime] = useState(() => Date.now())

  if (isLoading) return <CenteredLoading />

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="rounded-full bg-red-100 p-4">
              <XCircleIcon className="size-10 text-red-600" />
            </div>
            <h1 className="text-xl font-bold">{t('quoteReview.error.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('insuranceQuote.noActiveQuote')}</p>
            <Button
              variant="outline"
              onPress={() =>
                navigate({
                  to: '/carriers/$slug/driver-onboarding/dashboard',
                  params: { slug },
                })
              }
            >
              {t('insuranceQuote.backToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (data.isExpired) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-6 py-12 text-center">
            <div className="rounded-full bg-yellow-100 p-4">
              <ClockIcon className="size-10 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{t('quoteReview.expired.title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('quoteReview.expired.subtitle')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleDecision = (action: 'accept' | 'decline') => {
    respondToQuote.mutate(action, {
      onSuccess: () => {
        toast.success(
          t(action === 'accept' ? 'insuranceQuote.acceptedToast' : 'insuranceQuote.declinedToast'),
        )
        // Accepting leaves the driver owing a certificate of insurance, so land
        // them on the screen that asks for exactly that — not the full document
        // list, where the one outstanding slot sits under a wall of approved
        // ones. Declining still goes home.
        navigate({
          to:
            action === 'accept'
              ? '/carriers/$slug/driver-onboarding/insurance-certificate'
              : '/carriers/$slug/driver-onboarding/dashboard',
          params: { slug },
        })
      },
      onError: () => {
        toast.error(t('insuranceQuote.errorToast'))
      },
    })
  }

  const formattedAmount = formatCurrency(data.quoteAmount)
  const formattedMonthly = formatCurrency(data.monthlyEquivalent)
  const formattedWeekly = formatCurrency(data.weeklyEquivalent)

  const expiresDate = data.quoteExpiresAt ? new Date(data.quoteExpiresAt) : null
  const daysUntilExpiry = expiresDate
    ? Math.ceil((expiresDate.getTime() - pageLoadTime) / (1000 * 60 * 60 * 24))
    : null

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">{t('quoteReview.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('quoteReview.subtitle', { name: data.driverName })}
        </p>
      </div>

      {/* Quote Card */}
      <Card>
        <CardContent className="pt-6 text-center">
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            {t('quoteReview.annualPremium')}
          </p>
          <p className="mt-2 text-4xl font-bold text-primary">{formattedAmount}</p>
          <p className="mt-1 text-sm text-muted-foreground">/year</p>
          <p className="mt-3 text-sm text-muted-foreground">
            {formattedMonthly}
            {t('quoteReview.perMonthSuffix')} · {formattedWeekly}
            {t('quoteReview.perWeekSuffix')}
          </p>
          {expiresDate && (
            <div className="mt-4 flex items-center justify-center gap-1.5 text-sm">
              <ClockIcon className="size-3.5" />
              <span
                className={
                  daysUntilExpiry !== null && daysUntilExpiry <= 3
                    ? 'font-medium text-yellow-600'
                    : 'text-muted-foreground'
                }
              >
                {t('quoteReview.expiresOn', { date: formatUsDate(expiresDate) })}
              </span>
              {daysUntilExpiry !== null && daysUntilExpiry <= 3 && (
                <AlertTriangleIcon className="size-3.5 text-yellow-500" />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage Card */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
            {t('quoteReview.coverageTitle')}
          </h2>
          <ul className="mt-3 space-y-2.5">
            {/* Liability only: Allied writes commercial auto and nothing else on
                this path, and a single-truck operator owes no workers comp. */}
            <li className="flex items-center gap-2.5 text-sm">
              <ShieldCheckIcon className="size-4 shrink-0 text-primary" />
              {t('quoteReview.coverage.liability')}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="flex-1" isDisabled={respondToQuote.isPending}>
              {t('quoteReview.acceptQuote')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('quoteReview.confirmAccept.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('quoteReview.confirmAccept.description', { amount: formattedAmount })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('quoteReview.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDecision('accept')}>
                {t('quoteReview.confirmAccept.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="flex-1" isDisabled={respondToQuote.isPending}>
              {t('quoteReview.declineQuote')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('quoteReview.confirmDecline.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('quoteReview.confirmDecline.description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('quoteReview.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDecision('decline')}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('quoteReview.confirmDecline.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
